import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { useCart, useCartItems, useCartTotal } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { createOrder, Address } from '../../services/OrderService';
import { 
  doc, 
  getDoc,
  collection,
  getDocs,
  addDoc
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { getSiteSettings } from '../../services/FirestoreService';

const Checkout: React.FC = () => {
  const { state, dispatch } = useCart();
  const items = useCartItems();
  const total = useCartTotal();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [siteSettings, setSiteSettings] = useState<any>(null);
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [recaptchaCompleted, setRecaptchaCompleted] = useState(false);
  // State for address modal
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [newAddress, setNewAddress] = useState({
    title: '',
    address: '',
    city: '',
    district: '',
    neighborhood: '',
    phone: ''
  });

  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const settings = await getSiteSettings();
        setSiteSettings(settings);
      } catch (error) {
        console.error('Error fetching site settings:', error);
        // Use default value if there's an error
        setSiteSettings({ freeShippingThreshold: 750 });
      }
    };

    fetchSiteSettings();
  }, []);

  // Check reCAPTCHA verification when component mounts
  useEffect(() => {
    setShowRecaptcha(false);
    setRecaptchaCompleted(false);

    // Check if user has already been verified in the last 30 minutes
    const lastVerified = localStorage.getItem('recaptchaVerified');
    if (lastVerified) {
      const timeDiff = (Date.now() - parseInt(lastVerified)) / 60000; // in minutes
      if (timeDiff < 30) { // 30 minutes validity period
        setRecaptchaVerified(true);
      } else {
        // Clear expired verification
        localStorage.removeItem('recaptchaVerified');
        setRecaptchaVerified(false);
      }
    } else {
      setRecaptchaVerified(false);
    }
  }, []);

  // Load user addresses
  useEffect(() => {
    if (currentUser) {
      const loadAddresses = async () => {
        try {
          const addressesCollection = collection(db, `users/${currentUser.uid}/addresses`);
          const addressesSnapshot = await getDocs(addressesCollection);
          const addressesData = addressesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setAddresses(addressesData);
          
          // Select first address if available
          if (addressesData.length > 0) {
            setSelectedAddress(addressesData[0].id);
          }
        } catch (err) {
          setError('Adresler yüklenirken hata oluştu.');
        }
      };
      
      loadAddresses();
    }
  }, [currentUser]);

  // Handle adding a new address
  const handleAddAddress = async () => {
    if (!currentUser) return;

    try {
      const addressesCollection = collection(db, `users/${currentUser.uid}/addresses`);
      const docRef = await addDoc(addressesCollection, newAddress);
      
      // Add the new address to the state
      setAddresses(prev => [...prev, { id: docRef.id, ...newAddress }]);
      
      // Select the new address
      setSelectedAddress(docRef.id);
      
      // Close the modal and reset form
      setAddressModalOpen(false);
      setNewAddress({
        title: '',
        address: '',
        city: '',
        district: '',
        neighborhood: '',
        phone: ''
      });
    } catch (err) {
      setError('Adres eklenirken hata oluştu.');
    }
  };

  const handleCheckout = async () => {
    if (!currentUser) {
      // Redirect to login if user is not logged in
      navigate('/login');
      return;
    }
    
    if (!selectedAddress) {
      setError('Lütfen bir adres seçin');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Get the selected address details
      const selectedAddressDetails = addresses.find(addr => addr.id === selectedAddress);
      if (!selectedAddressDetails) {
        throw new Error('Seçilen adres bulunamadı');
      }
      
      // Create an order in Firestore
      const orderData = {
        userId: currentUser.uid,
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          discountedPrice: item.discountedPrice,
          quantity: item.quantity,
          image: item.image
        })),
        total: total + (total >= (siteSettings?.freeShippingThreshold || 750) ? 0 : 29.90), // Add shipping cost if applicable
        address: selectedAddressDetails as Address,
        paymentMethod,
        status: 'pending' as const
      };
      
      await createOrder(orderData);
      
      // Clear cart after successful payment
      dispatch({ type: 'CLEAR_CART' });
      setSuccess(true);
    } catch (err) {
      setError('Sipariş işlemi başarısız oldu. Lütfen tekrar deneyin.');
      console.error('Checkout error:', err);
    }
    
    setLoading(false);
  };

  // Handle reCAPTCHA verification
  const handleRecaptchaVerification = () => {
    // In a real implementation, you would send the reCAPTCHA token to your backend for verification
    // For now, we'll simulate verification and store it in local storage for the session
    setRecaptchaVerified(true);
    setShowRecaptcha(false);
    setRecaptchaCompleted(false); // Reset the UI check state

    // Store verification timestamp to avoid re-verification for a certain period
    localStorage.setItem('recaptchaVerified', Date.now().toString());
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Payment successful! Your order has been placed.
          </Alert>
          <Typography variant="h5" gutterBottom>
            Thank you for your order!
          </Typography>
          <Typography variant="body1">
            Your order will be processed and shipped soon.
          </Typography>
          <Button 
            variant="contained" 
            href="/"
            sx={{ mt: 2 }}
          >
            Continue Shopping
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Checkout
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {!currentUser ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Sipariş vermek için giriş yapmanız gerekmektedir.
            </Alert>
            <Button 
              variant="contained" 
              color="primary" 
              size="large"
              onClick={() => navigate('/login')}
            >
              Giriş Yap
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 65%', minWidth: '300px' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Shipping Address
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="address-select-label">Select Address</InputLabel>
                    <Select
                      labelId="address-select-label"
                      id="address-select"
                      value={selectedAddress}
                      label="Select Address"
                      onChange={(e) => setSelectedAddress(e.target.value)}
                    >
                      {addresses.map((address) => (
                        <MenuItem key={address.id} value={address.id}>
                          {address.title} - {address.address}, {address.city}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {addresses.length === 0 && (
                    <Button 
                      variant="outlined" 
                      onClick={() => setAddressModalOpen(true)}
                      sx={{ mb: 2 }}
                    >
                      Add New Address
                    </Button>
                  )}
                  
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Payment Method
                  </Typography>
                  
                  <FormControl fullWidth>
                    <Select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <MenuItem value="cod">Cash on Delivery</MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            </Box>
            
            <Box sx={{ flex: '1 1 30%', minWidth: '250px' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Order Summary
                  </Typography>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              {item.name} (x{item.quantity})
                            </TableCell>
                            <TableCell align="right">
                              ₺{((item.discountedPrice ?? item.price) * item.quantity).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell>Subtotal</TableCell>
                          <TableCell align="right">₺{total.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Shipping</TableCell>
                          <TableCell align="right">
                            {total >= (siteSettings?.freeShippingThreshold || 750) ? 'FREE' : '₺29.90'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell align="right">
                            <strong>
                              ₺{(total + (total >= (siteSettings?.freeShippingThreshold || 750) ? 0 : 29.90)).toFixed(2)}
                            </strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {recaptchaVerified ? (
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={handleCheckout}
                      disabled={loading || items.length === 0}
                    >
                      {loading ? 'Processing...' : `Siparişi Ver ₺${(total + (total >= (siteSettings?.freeShippingThreshold || 750) ? 0 : 29.90)).toFixed(2)}`}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => setShowRecaptcha(true)}
                      disabled={loading || items.length === 0}
                    >
                      {loading ? 'Processing...' : `Siparişi Ver ₺${(total + (total >= (siteSettings?.freeShippingThreshold || 750) ? 0 : 29.90)).toFixed(2)}`}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}
      </Box>
      
      {/* reCAPTCHA Verification Dialog */}
      <Dialog
        open={showRecaptcha}
        onClose={() => {
          setShowRecaptcha(false);
          setRecaptchaCompleted(false);
        }}
      >
        <DialogTitle>reCAPTCHA Verification</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Typography>Please verify that you are not a robot</Typography>
            <Box
              sx={{
                mt: 2,
                border: '1px solid #ccc',
                borderRadius: '4px',
                p: 2,
                backgroundColor: '#f9f9f9',
                height: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                onClick={() => setRecaptchaCompleted(true)}
                sx={{
                  cursor: 'pointer',
                  width: 70,
                  height: 70,
                  border: recaptchaCompleted ? '2px solid #4caf50' : '1px solid #9e9e9e',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: recaptchaCompleted ? '#e8f5e9' : '#fff'
                }}
              >
                {recaptchaCompleted ? (
                  <Typography color="success.main" fontWeight="bold">✓</Typography>
                ) : (
                  <Typography fontSize="12px">I'm not a robot</Typography>
                )}
              </Box>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Click the box to verify
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowRecaptcha(false);
              setRecaptchaCompleted(false);
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRecaptchaVerification}
            variant="contained"
            disabled={!recaptchaCompleted}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Creation Modal */}
      <Dialog open={addressModalOpen} onClose={() => setAddressModalOpen(false)}>
        <DialogTitle>Add New Address</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Address Title (e.g., Home, Work)"
            fullWidth
            variant="outlined"
            value={newAddress.title}
            onChange={(e) => setNewAddress({...newAddress, title: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Address"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newAddress.address}
            onChange={(e) => setNewAddress({...newAddress, address: e.target.value})}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="City"
              fullWidth
              variant="outlined"
              value={newAddress.city}
              onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
            />
            <TextField
              label="District"
              fullWidth
              variant="outlined"
              value={newAddress.district}
              onChange={(e) => setNewAddress({...newAddress, district: e.target.value})}
            />
          </Box>
          <TextField
            margin="dense"
            label="Neighborhood"
            fullWidth
            variant="outlined"
            value={newAddress.neighborhood}
            onChange={(e) => setNewAddress({...newAddress, neighborhood: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Phone Number"
            fullWidth
            variant="outlined"
            value={newAddress.phone}
            onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressModalOpen(false)}>Cancel</Button>
          <Button onClick={handleAddAddress} variant="contained">Add Address</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Checkout;